import { isTabInFocus } from '../utils/utils';
import { action, autorun, computed, makeObservable, observable } from 'mobx';
import { IFilterItem } from '@shared/ui-react/CheckboxListDropdown';
import { IVehiclesGrouped, VehiclesService } from '@shared/vehicles/VehiclesService';
import { PageStateUtils } from '@shared/user-state/PageStateUtils';
import { LStorage } from '@shared/Storage';
import { alphaNumSort } from '@shared/utils/utils-sort';
import { DateTime } from 'luxon';

interface IVehiclesStoreState {
    showVehiclesOnMap: boolean;
    showVendors: number[];
    showTypes: number[];
    // save inverse selection also, so that when new vendors/types are added, they default to checked on live state restore
    // but historic saved views will still show just what was selected at that time
    hiddenVendors: number[];
    hiddenTypes: number[];
    sortKey: string;
    trailOpacity: number;
    isHeatmapActive: boolean;
    filterCalibration: 'none' | 'outside' | '2_weeks' | 'no_calibration' | string;
    filterActiveInLastDays?: number;
}

export class VehiclesStore implements IStateful<IVehiclesStoreState> {
    @observable vehicles: IVehicle[] = [];
    @observable positions: IVehicle[] = [];
    @observable sortKey = 'name';
    @observable isLoading = false;
    @observable isInitialLoad = true;
    @observable filterPinned = false;
    @observable filterOpened = false;

    @observable showVehiclesOnMap = true;
    @observable filterText = '';
    @observable filterCalibration: 'none' | 'outside' | '2_weeks' | 'no_calibration' = 'none';
    @observable filterActiveInLastDays: number = null;
    @observable types = new Map<number, IFilterItem>(Object.entries(VehiclesService.TypesIndexedOptions).map(e => [parseInt(e[0]), { ...e[1] }]));
    @observable vendors = new Map<number, IFilterItem>(Object.entries(VehiclesService.VendorsIndexedOptions).map(e => [parseInt(e[0]), { ...e[1] }]));
    @observable trailOpacity = 100;
    @observable isHeatmapActive: boolean = false;
    @observable loadedLogpointsByVehicleId: number = null; // loaded by single vehicle, so we know what to clear

    private interval = null;
    private firstLoad = true;
    private localStorageKey: string = null;
    private updateIntervalMs: number = 15000;

    @computed
    get vehiclesFilteredAndSorted(): IVehicle[] {
        let filtered = this.vehicles.filter(
            v => this.activeVendors.includes(v.vendor) && this.activeTypes.includes(v.type) && v.nameLatin.toLowerCase().includes((this.filterText || '').toLowerCase())
        );

        if (this.filterActiveInLastDays) {
            filtered = filtered.filter(v => {
                if (!v.lastReport) return false;
                return v.lastReport > DateTime.now().minus({ days: this.filterActiveInLastDays }).toMillis();
            });
        }

        // by calibration status
        if (this.filterCalibration && this.filterCalibration !== 'none') {
            filtered = filtered.filter(v => {
                if (this.filterCalibration === 'no_calibration') {
                    return v.calibrationWarning && v.calibrationDifference && v.calibrationDifference[0] === null;
                } else if (this.filterCalibration === '2_weeks') {
                    return v.calibrationWarning && v.calibrationDifference && v.calibrationDifference[0] !== null;
                } else if (this.filterCalibration === 'outside') {
                    if (v.calibrationDifference && v.calibrationDifference[0] === null) return false;
                    const calibStatus = VehiclesService.getCalibrationStatus(v);
                    return !calibStatus.allDeltasValid;
                }
            });
        }

        return filtered.sort((a, b) => {
            if (this.sortKey === 'lastReport') {
                [a, b] = [b, a];
                return alphaNumSort(a[this.sortKey] || 0, b[this.sortKey] || 0);
            }
            return alphaNumSort(a[this.sortKey], b[this.sortKey]);
        });
    }

    @computed
    get vehiclesFilteredGroupedByStatus(): IVehiclesGrouped {
        return VehiclesService.groupByStatus(this.vehiclesFilteredAndSorted);
    }

    @computed
    private get activeVendors(): number[] {
        return Array.from(this.vendors.values())
            .filter(v => v.active)
            .map(v => v.value) as number[];
    }

    @computed
    private get activeTypes(): number[] {
        return Array.from(this.types.values())
            .filter(v => v.active)
            .map(v => v.value) as number[];
    }

    @computed
    private get hiddenVendors(): number[] {
        return Array.from(this.vendors.values())
            .filter(v => !v.active)
            .map(v => v.value) as number[];
    }

    @computed
    private get hiddenTypes(): number[] {
        return Array.from(this.types.values())
            .filter(v => !v.active)
            .map(v => v.value) as number[];
    }

    @computed
    get numberOfAppliedFilters(): number {
        let count = 0;
        if (!this.showVehiclesOnMap) count += 1;
        if (this.filterText.length) count += 1;
        if (this.filterActiveInLastDays > 0) count += 1;
        if (Array.from(this.types.values()).some(v => v.active === false)) count += 1;
        if (Array.from(this.vendors.values()).some(v => v.active === false)) count += 1;
        return count;
    }

    constructor(opts?: { localStorageKey?: string; updateIntervalMs?: number; initialVehicles?: IVehicle[] }) {
        makeObservable(this);
        if (opts) {
            this.localStorageKey = opts.localStorageKey ? opts.localStorageKey : null;
            this.updateIntervalMs = opts.updateIntervalMs ? opts.updateIntervalMs : null;

            if (opts.initialVehicles) {
                this.vehicles = opts.initialVehicles;
            }
        }

        if (this.localStorageKey) {
            autorun(
                () => {
                    const state = this.stateful.getCurrentState();
                    LStorage.setItem(this.localStorageKey, state);
                },
                { delay: 100 }
            );

            this.stateful.restoreState();
        }

        makeObservable(this);
    }

    // fetches all vehicles, even on hold and with no positions, needed for sidebar list
    fetchAll(): JQueryPromise<IVehicle[]> {
        this.isLoading = true;
        return VehiclesService.fetchForActiveProject()
            .always(() => {
                this.isLoading = false;
                this.isInitialLoad = false;
            })
            .done(vehicles => {
                this.vehicles = vehicles;
            });
    }

    startInterval(updateIntervalMs: number = this.updateIntervalMs) {
        if (this.interval) clearInterval(this.interval);

        this.interval = setInterval(() => {
            // skip if window is not in focus
            if (!isTabInFocus()) return;

            this.fetchAll();
        }, updateIntervalMs);
    }

    getVehicleById(id: number): IVehicle {
        return this.vehicles.find(v => v.id === id);
    }

    getVehicleByUuid(uuid: string): IVehicle {
        return this.vehicles.find(v => v.uuid === uuid);
    }

    @action
    clearFilter = () => {
        this.vendors.forEach(v => (v.active = true));
        this.types.forEach(v => (v.active = true));
        this.showVehiclesOnMap = true;
        this.filterCalibration = 'none';
        this.isHeatmapActive = false;
        this.filterActiveInLastDays = null;
        this.filterText = '';
    };

    stateful = {
        defaultState: {
            showVehiclesOnMap: true,
            showVendors: VehiclesService.VendorNames.map((v, i) => i),
            showTypes: VehiclesService.TypeNames.map((v, i) => i),
            hiddenVendors: [],
            hiddenTypes: [],
            sortKey: 'name',
            filterCalibration: 'none',
            isFilterPanelCollapsed: false,
            filterActiveInLastDays: null,
            trailOpacity: 100,
            isHeatmapActive: false,
        },

        getCurrentState: (): IVehiclesStoreState => {
            return {
                showVehiclesOnMap: this.showVehiclesOnMap,
                showVendors: this.activeVendors,
                showTypes: this.activeTypes,
                hiddenVendors: this.hiddenVendors,
                hiddenTypes: this.hiddenTypes,
                sortKey: this.sortKey,
                filterCalibration: this.filterCalibration,
                filterActiveInLastDays: this.filterActiveInLastDays,
                trailOpacity: this.trailOpacity,
                isHeatmapActive: this.isHeatmapActive,
            };
        },

        restoreDefaultState: (): void => {
            this.stateful.restoreState(this.stateful.defaultState);
            this.clearFilter();
        },

        restoreState: action((state?: IVehiclesStoreState, isLive?: boolean): void => {
            if (!state) {
                state = LStorage.getOrDefault(this.localStorageKey, null);
                isLive = true;
            }

            if (!state) return null;

            if (PageStateUtils.stateHasKey<IVehiclesStoreState>(state, 'showVehiclesOnMap')) {
                this.showVehiclesOnMap = state.showVehiclesOnMap;
            }

            if (PageStateUtils.stateHasKey<IVehiclesStoreState>(state, 'sortKey')) {
                this.sortKey = state.sortKey;
            }

            if (PageStateUtils.stateHasKey<IVehiclesStoreState>(state, 'trailOpacity')) {
                this.trailOpacity = state.trailOpacity;
            }

            if (PageStateUtils.stateHasKey<IVehiclesStoreState>(state, 'isHeatmapActive')) {
                this.isHeatmapActive = state.isHeatmapActive;
            }

            if (isLive) {
                // check type.hiddenTypes comment for reasoning
                const hiddenTypes = Array.isArray(state.hiddenTypes) ? state.hiddenTypes : [];
                this.types.forEach(t => (t.active = !hiddenTypes.includes(t.value as number)));
            } else if (PageStateUtils.stateHasKey<IVehiclesStoreState>(state, 'showTypes') && Array.isArray(state.showTypes) && state.showTypes.length) {
                this.types.forEach(t => (t.active = state.showTypes.includes(t.value as number)));
            }

            if (isLive) {
                const hiddenVendors = Array.isArray(state.hiddenVendors) ? state.hiddenVendors : [];
                this.vendors.forEach(v => (v.active = !hiddenVendors.includes(v.value as number)));
            } else if (PageStateUtils.stateHasKey<IVehiclesStoreState>(state, 'showVendors') && Array.isArray(state.showVendors) && state.showVendors.length) {
                this.vendors.forEach(v => (v.active = state.showVendors.includes(v.value as number)));
            }

            if (PageStateUtils.stateHasKey<IVehiclesStoreState>(state, 'filterCalibration')) {
                this.filterCalibration = state.filterCalibration as any;
            }

            if (
                PageStateUtils.stateHasKey<IVehiclesStoreState>(state, 'filterActiveInLastDays') &&
                $.isNumeric(state.filterActiveInLastDays) &&
                state.filterActiveInLastDays >= 1
            ) {
                this.filterActiveInLastDays = state.filterActiveInLastDays;
            }
        }),
    };
}
